import { useState } from 'react'
import { Header } from '../components/layout/Header'
import { LanguageSelector } from '../components/common/LanguageSelector'
import { useI18n } from '../contexts/I18nContext'

export default function LanguageSettings() {
  const { t, language, languages } = useI18n()
  const [showTranslations, setShowTranslations] = useState(false)

  const currentLanguage = languages.find((l) => l.code === language)

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F9FAFB' }}>
      <Header />

      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ margin: '0 0 8px 0', fontSize: '24px', color: '#1F2937' }}>
            {t('settings.language', 'Language Settings')}
          </h1>
          <p style={{ margin: 0, color: '#6B7280' }}>
            Choose your preferred language for the application interface
          </p>
        </div>

        {/* Current Language */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
            padding: '24px',
            marginBottom: '24px',
          }}
        >
          <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#1F2937' }}>
            Current Language
          </h2>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
            <span style={{ fontSize: '48px' }}>{currentLanguage?.flag}</span>
            <div>
              <div style={{ fontSize: '24px', fontWeight: 600, color: '#1F2937' }}>
                {currentLanguage?.nativeName}
              </div>
              <div style={{ color: '#6B7280' }}>{currentLanguage?.name}</div>
            </div>
          </div>

          <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#374151', fontWeight: 500 }}>
            Select a different language:
          </h3>
          <LanguageSelector variant="inline" />
        </div>

        {/* Available Languages */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
            padding: '24px',
            marginBottom: '24px',
          }}
        >
          <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#1F2937' }}>
            Available Languages
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '16px' }}>
            {languages.map((lang) => (
              <div
                key={lang.code}
                style={{
                  padding: '16px',
                  backgroundColor: language === lang.code ? '#EBF5FF' : '#F9FAFB',
                  borderRadius: '8px',
                  border: `1px solid ${language === lang.code ? '#4A90E2' : '#E5E7EB'}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '32px' }}>{lang.flag}</span>
                  <div>
                    <div style={{ fontWeight: 600, color: '#1F2937' }}>{lang.nativeName}</div>
                    <div style={{ fontSize: '13px', color: '#6B7280' }}>{lang.name}</div>
                  </div>
                  {language === lang.code && (
                    <span
                      style={{
                        marginLeft: 'auto',
                        backgroundColor: '#4A90E2',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                      }}
                    >
                      Active
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Translation Preview */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
            padding: '24px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ margin: 0, fontSize: '18px', color: '#1F2937' }}>Translation Preview</h2>
            <button
              onClick={() => setShowTranslations(!showTranslations)}
              style={{
                padding: '8px 16px',
                backgroundColor: showTranslations ? '#EF4444' : '#4A90E2',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              {showTranslations ? 'Hide Translations' : 'Show Translations'}
            </button>
          </div>

          {showTranslations && (
            <div style={{ maxHeight: '400px', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F9FAFB' }}>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #E5E7EB' }}>Key</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #E5E7EB' }}>
                      Translation ({currentLanguage?.nativeName})
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    'app.name',
                    'app.tagline',
                    'nav.dashboard',
                    'nav.markets',
                    'nav.reports',
                    'nav.analytics',
                    'nav.compliance',
                    'nav.settings',
                    'common.save',
                    'common.cancel',
                    'common.delete',
                    'common.search',
                    'common.loading',
                    'dashboard.title',
                    'dashboard.welcome',
                    'markets.title',
                    'reports.title',
                    'analytics.title',
                    'compliance.title',
                    'query.title',
                    'settings.title',
                    'status.active',
                    'status.completed',
                    'status.pending',
                    'message.saved',
                    'message.error',
                  ].map((key) => (
                    <tr key={key} style={{ borderBottom: '1px solid #E5E7EB' }}>
                      <td style={{ padding: '10px', fontFamily: 'monospace', color: '#6B7280' }}>{key}</td>
                      <td style={{ padding: '10px', color: '#1F2937' }}>{t(key)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!showTranslations && (
            <div style={{ color: '#6B7280', fontSize: '14px' }}>
              Click "Show Translations" to preview how your selected language translates key application strings.
            </div>
          )}
        </div>

        {/* Info */}
        <div
          style={{
            marginTop: '24px',
            padding: '16px',
            backgroundColor: '#FEF3C7',
            borderRadius: '8px',
            border: '1px solid #FCD34D',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span style={{ fontSize: '20px' }}>ℹ️</span>
            <div>
              <div style={{ fontWeight: 600, color: '#92400E', marginBottom: '4px' }}>Note</div>
              <div style={{ color: '#A16207', fontSize: '14px' }}>
                Language settings are saved locally in your browser. The application will remember your language
                preference across sessions. Some content like data from external sources may still appear in
                English.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
